import { ActiveListOrder, ArchivedListOrder, OrdersState, OrdersType } from "@app/modules/orders/entities/orders.entities";
import * as OrdersAction from "@app/modules/orders/store/actions/orders.action";
import { GlobalAdapter } from "@core/store/adapters/global.adapter";
import { createEntityAdapter, EntityAdapter, EntityState } from "@ngrx/entity";
import { Action, createFeatureSelector, createReducer, createSelector, on } from "@ngrx/store";

export const ordersPerPage: number = 50;

export type ListOrder = ArchivedListOrder | ActiveListOrder;

function childOrdersAdapterSelectId(pp: ListOrder): string {
  return pp.id;
}

export const childOrdersAdapter: EntityAdapter<ListOrder> = createEntityAdapter<ListOrder>({
  selectId: childOrdersAdapterSelectId,
  sortComparer: (ppA, ppB) => (ppA.id > ppB.id ? 1 : -1),
});

interface OrdersStateReducer extends OrdersState, EntityState<ListOrder> {}

const initialState: OrdersStateReducer = childOrdersAdapter.getInitialState({
  selectedOrder: null,
  selectedPendingOrder: null,
  selectedBoxPendingOrder: null,
  ui: {
    loaderBtnCancel: false,
    loadingOrderDetail: false,
    loadingOrderList: false,
    loadingExcelExport: false,
    isModalCancelShow: false,
    hasActiveData: false,
    hasBoxPendingData: false,
    hasPendingData: false,
    hasArchivedData: false,
    lastArchivedOrdersPage: false,
    openExcelDialog: false,
    openDuplicateOrderDialog: false,
    openColumnSettingsDialog: false,
    columns: [],
    showRemoveDebtModal: false,
    archivedOrdersCursor: null,
    previousArchivedOrdersCursors: [],
    childOrderDetail: true,
  },
  orderDuplication: {
    aproximateCost: null,
    body: null,
    parsedBody: null,
    newTotalValue: "",
  },
  searchInput: "",
  activeOrdersList: [],
  archivedOrdersList: [],
  pendingOrdersList: [],
  selectedOrderId: null,
  pendingOrdersSelectedPickingPoint: null,
  ordersType: OrdersType.ACTIVE,
} as OrdersState);

const ordersReducer = createReducer(
  initialState,
  on(OrdersAction.clearAllActiveOrders, () => initialState),
  on(OrdersAction.setSelectedOrder, (state, { payload }) => ({ ...state, selectedOrder: payload, selectedPendingOrder: null })),
  on(OrdersAction.setActiveOrdersList, (state, { payload }) => ({ ...state, activeOrdersList: payload })),
  on(OrdersAction.setPendingOrdersList, (state, { payload }) => ({ ...state, pendingOrdersList: payload })),
  on(OrdersAction.setOrderDuplication, (state, { payload }) => ({ ...state, orderDuplication: { ...state.orderDuplication, ...payload } })),
  on(OrdersAction.setArchivedOrdersList, (state, { payload }) => ({ ...state, archivedOrdersList: payload })),
  on(OrdersAction.setBoxPendingOrdersList, (state, { payload }) => ({ ...state, pendingBoxOrdersList: payload })),
  on(OrdersAction.setBoxSelectedPendingOrder, (state, { payload }) => ({
    ...state,
    selectedBoxPendingOrder: payload,
    selectedOrder: null,
  })),
  on(OrdersAction.setBoxPendingOrdersSelectedPickingPoint, (state, { payload }) => ({
    ...state,
    pendingOrdersSelectedPickingPoint: payload,
  })),
  on(OrdersAction.toggleOrdersType, (state, { payload }) => ({ ...state, ordersType: payload, searchInput: "" })),
  on(OrdersAction.uiOrders, (state, { ui }) => GlobalAdapter.setUi(ui, state)),
  on(OrdersAction.setChildOrders, (state, { payload }) => childOrdersAdapter.setAll(payload, state)),
  on(OrdersAction.inputSearch, (state, { inputSearch: searchInput }) => ({ ...state, searchInput }))
);

export function OrdersReducer(state: OrdersStateReducer, action: Action) {
  return ordersReducer(state, action);
}

export const selectOrdersReducerState = createFeatureSelector<OrdersStateReducer>("activeOrders");

export const { selectEntities: selectChildOrders } = childOrdersAdapter.getSelectors(selectOrdersReducerState);
export const getOrdersUi = createSelector(selectOrdersReducerState, ({ ui }) => ui);
export const getSelectedOrder = createSelector(selectOrdersReducerState, ({ selectedOrder }) => selectedOrder);
export const getSelectedOrderId = createSelector(selectOrdersReducerState, ({ selectedOrderId }) => selectedOrderId);
export const getSelectedPendingOrder = createSelector(selectOrdersReducerState, ({ selectedPendingOrder }) => selectedPendingOrder);
export const getSelectedBoxPendingOrder = createSelector(
  selectOrdersReducerState,
  ({ selectedBoxPendingOrder }) => selectedBoxPendingOrder
);
export const getOrderDuplicationData = createSelector(selectOrdersReducerState, ({ orderDuplication }) => orderDuplication);
export const getAllActiveOrders = createSelector(selectOrdersReducerState, ({ activeOrdersList }) => activeOrdersList);
export const getAllArchivedOrders = createSelector(selectOrdersReducerState, ({ archivedOrdersList }) => archivedOrdersList);
export const getAllPendingOrders = createSelector(selectOrdersReducerState, ({ pendingOrdersList }) => pendingOrdersList);
export const getAllBoxPendingOrders = createSelector(selectOrdersReducerState, ({ pendingBoxOrdersList }) => pendingBoxOrdersList);
export const getOrdersType = createSelector(selectOrdersReducerState, ({ ordersType }) => ordersType);
export const getArchivedOrdersCursor = createSelector(selectOrdersReducerState, ({ ui }) => ui.archivedOrdersCursor);
export const getPreviousArchivedOrdersCursors = createSelector(selectOrdersReducerState, ({ ui }) => ui.previousArchivedOrdersCursors);
export const getSearchInput = createSelector(selectOrdersReducerState, ({ searchInput }) => searchInput);
export const getPendingOrdersSelectedPickingPoint = createSelector(
  selectOrdersReducerState,
  ({ pendingOrdersSelectedPickingPoint }) => pendingOrdersSelectedPickingPoint
);
